import { Controller } from "@hotwired/stimulus"

// Keeps the sidebar's calories and macros in step with the ingredients as you
// edit them.
//
// The arithmetic stays on the server. Recomputing it in JS would be faster and
// would mean two implementations of the thing the brief calls core IP — and
// the browser's copy would be the one nobody unit-tests. So the form posts
// itself to a compute-only endpoint and swaps in what comes back.
export default class extends Controller {
  static targets = ["panel"]
  static values = { url: String, recipeId: String }

  connect() {
    this.controller = null
    // What the page was rendered with, so the first stray change event does
    // not ask the server to confirm the figures already on screen.
    this.lastSignature = this.signature()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.controller?.abort()
  }

  // A change event — a food chosen, a unit picked, servings edited. These are
  // decisions rather than half-typed states, so they go through quickly.
  //
  // The quantity is deliberately NOT here. A number input fires change as
  // well as input while you type, so letting it through meant the 500ms
  // debounce below was racing a 120ms one and losing — which is why it still
  // refreshed on every keystroke.
  changed(event) {
    const name = event.target.name || ""

    // Only the fields the totals depend on. The name, description, method,
    // photo, cuisine and every checkbox fire change too, and none of them can
    // move a calorie — letting them reach the signature check meant the
    // spinner flashed for a request that was then correctly skipped.
    if (!this.affectsTotals(name)) return

    if (name.includes("[entered_quantity]")) return

    // A row with no food chosen yet contributes nothing, so there is nothing
    // to recompute — and a brand new row fires change on its empty select as
    // the unit list is built. Also covers the combobox clearing itself before
    // it writes the new value.
    if (name.includes("[food_id]") && !event.target.value) return
    if (name.includes("[entered_unit]") && !this.rowHasFood(event.target)) return

    this.refresh(120)
  }

  // Typing a quantity. Nothing else — the food search box holds no form
  // value, so recomputing as you type through it asks the same question over
  // and over and answers it with the same number.
  typed(event) {
    if (!event.target.name?.includes("[entered_quantity]")) return

    // A quantity with no food is a number attached to nothing.
    if (!this.rowHasFood(event.target)) return

    // Clearing the box on the way to typing a new number is not a state
    // worth asking about — the signature check would catch it a moment
    // later, but not before the spinner had already gone up.
    if (!Number(event.target.value)) return

    // Long enough to type "100" as one edit rather than as 1, 10, 100.
    this.refresh(500)
  }

  // The fields a calorie total can actually depend on.
  //
  // Named rather than inferred, so a new field on the form does not quietly
  // start triggering requests. Servings is here because it divides
  // everything; the rest of the recipe — name, method, photo, cuisine, meal
  // types — cannot change a number in the panel.
  affectsTotals(name) {
    return name === "recipe[servings]" ||
      name.includes("[food_id]") ||
      name.includes("[entered_quantity]") ||
      name.includes("[entered_unit]")
  }

  // Whether the row this field belongs to has a food chosen. An ingredient
  // needs one before any of its other fields mean anything.
  rowHasFood(field) {
    const row = field.closest("[data-ingredients-target='row']")

    return Boolean(row?.querySelector("select[name*='[food_id]']")?.value)
  }

  // The panel is marked stale IMMEDIATELY, not when the request goes out.
  // Someone who has just changed a number wants to know the figures below are
  // about to move — waiting out the debounce first leaves the panel looking
  // authoritative while showing the previous recipe.
  refresh(delay) {
    // Called both directly with a delay and as an action, which hands us the
    // event instead — a removal wants the short delay either way.
    const wait = typeof delay === "number" ? delay : 120

    // Nothing to do if the ingredients have not actually moved. Several
    // things fire change on a row without altering what it contributes — the
    // unit picker syncing itself, a food being re-selected, a blur — and each
    // one used to put the spinner up and take it down again a moment later
    // over an identical answer.
    if (this.signature() === this.lastSignature) return

    this.panelTarget.classList.add("is-recalculating")

    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.fetchTotals(), wait)
  }

  // What the totals actually depend on: the servings, and each row's food,
  // quantity and unit. Anything else on the form — the name, the method, the
  // photo — cannot change a calorie.
  signature() {
    const parts = [ this.element.querySelector("[name='recipe[servings]']")?.value ]

    for (const row of this.element.querySelectorAll("[data-ingredients-target='row']")) {
      // A row hidden by the remove button is marked for destruction and no
      // longer counts, so its values must not appear here.
      if (row.style.display === "none") continue

      const foodId = row.querySelector("select[name*='[food_id]']")?.value
      const quantity = row.querySelector("input[name*='[entered_quantity]']")?.value

      // A row with no food, or none of a food, contributes nothing — so it
      // must not appear in the signature at all. Adding a blank row and
      // watching its quantity go from "" to "0" is not a change to the
      // recipe, and it used to spend a request finding that out.
      if (!foodId || !Number(quantity)) continue

      parts.push([
        foodId,
        quantity,
        row.querySelector("select[name*='[entered_unit]']")?.value
      ].join("/"))
    }

    return parts.join("|")
  }

  async fetchTotals() {
    // Abort the one in flight. Responses can arrive out of order, and the
    // last one to LAND would otherwise win rather than the last one sent —
    // which shows totals for an ingredient list you have already changed.
    this.controller?.abort()
    this.controller = new AbortController()

    // Recorded at send time, not at schedule time: the debounce means the
    // form may have moved again since, and this is the state being asked
    // about.
    this.lastSignature = this.signature()

    // Read the form at REQUEST time, not when the event fired — the debounce
    // means several edits may have landed since.
    const body = new FormData(this.element)
    // A file field in the payload makes this a multipart upload of the photo
    // on every keystroke, for a request that only reads numbers.
    body.delete("recipe[photo]")
    // The rows inside <template> are inert and never collected, but the
    // combobox's hidden select IS — it holds the chosen food_id, which is the
    // whole point.
    // _method is why this did not work at all. The edit form is a PATCH, so
    // form_with renders a hidden _method=patch — FormData collects it, Rails
    // honours the override, and the POST arrived as a PATCH to a path that
    // only answers POST. The server was computing correctly the whole time
    // and never saw the request.
    body.delete("_method")

    // Which recipe this is editing, so the panel can show what CHANGED
    // rather than only what the figures now are.
    if (this.recipeIdValue) body.set("recipe_id", this.recipeIdValue)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body,
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest",
          // Sent explicitly rather than relying on the form's hidden field.
          // Tests run with forgery protection off, so a missing token passes
          // every spec and 422s in the browser — which is exactly what it
          // did.
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content || ""
        },
        signal: this.controller.signal
      })
      if (!response.ok) {
        this.lastSignature = null
        this.settle()
        return
      }

      const parsed = new DOMParser()
        .parseFromString(await response.text(), "text/html")

      this.panelTarget.replaceChildren(...parsed.body.childNodes)
      this.restoreRowFigureToggle()
      this.applyRowFigures()
      this.settle()
    } catch (error) {
      // An aborted request is the NEXT keystroke's doing, so the panel stays
      // marked stale — its replacement is already on the way. A genuine
      // failure clears the mark instead: the figures are one edit behind
      // rather than arriving, and leaving them pulsing forever would promise
      // an update that is not coming.
      if (error.name !== "AbortError") {
        // Forget what was asked, so the next change asks again rather than
        // being skipped as a repeat of a request that never landed.
        this.lastSignature = null
        this.settle()
      }
    }
  }

  settle() {
    this.panelTarget.classList.remove("is-recalculating")
  }

  // Show or hide the per-row figures.
  //
  // The class goes on the FORM, not on the panel, because the rows it
  // governs live in the form and the panel is replaced on every
  // recalculation — a class there would be wiped by the next keystroke.
  toggleRowFigures(event) {
    this.element.classList.toggle("shows-ingredient-macros", event.target.checked)

    // The cells are filled by whatever the last response carried, and on a
    // freshly loaded page that is the server's first render — which nothing
    // had read yet, so turning the toggle on showed empty columns until you
    // happened to edit something.
    this.applyRowFigures()
  }

  // The checkbox is inside the panel, so a swap gives us a fresh unchecked
  // one. Put it back to whatever the form says it is.
  restoreRowFigureToggle() {
    const box = this.panelTarget.querySelector("[data-action*='toggleRowFigures']")

    if (box) box.checked = this.element.classList.contains("shows-ingredient-macros")
  }

  // Each row's own contribution, written beside the row.
  //
  // The figures come back with the panel, in the order the rows were
  // submitted — which is DOM order, since that is what FormData walks. A
  // group heading or a row with no food comes back null and gets nothing,
  // keeping the array aligned with the rows rather than needing ids.
  applyRowFigures() {
    const source = this.panelTarget.querySelector("[data-ingredient-macros]")
    if (!source) return

    let figures
    try {
      figures = JSON.parse(source.textContent)
    } catch {
      return
    }

    // ONLY THE ROWS THE SERVER WAS SENT.
    //
    // The figures are ordered as the rows were SUBMITTED, and a row marked
    // for destruction is not submitted — recipes_controller#nutrition_params
    // rejects it, and the model would reject it again. But the DOM keeps
    // that row: removal hides it, so its macro cell is still here.
    //
    // Pairing every cell to figures[index] therefore shifted by one for each
    // deletion above it, and every visible row under a deleted one showed
    // its neighbour's calories — with the "≈450 g cooked" chips mislabelled
    // by the same offset. Confidently wrong numbers in the panel whose only
    // job is saying which ingredient carries them.
    //
    // Filtering the cells by the same rule the server filters rows by is
    // what keeps the two lists describing the same things. A request spec
    // cannot see this: the JSON is correct in isolation, and only the
    // browser pairs it with cells. Found by the codebase review, 16 Sep.
    const live = (node) => {
      const row = node.closest("[data-ingredients-target='row']")
      if (!row || row.style.display === "none") return false

      const destroy = row.querySelector("[data-ingredients-target='destroy']")
      return destroy?.value !== "1"
    }

    const cells = [...this.element.querySelectorAll("[data-ingredients-target='macros']")].filter(live)
    const conversions = [...this.element.querySelectorAll("[data-ingredients-target='conversion']")].filter(live)

    cells.forEach((cell, index) => {
      const row = figures[index]
      this.paintConversion(conversions[index], row?.converted)

      if (!row) {
        cell.replaceChildren()
        return
      }

      // One line: calories, then each macro with its initial.
      //
      // Two stacked lines with a column per macro lined the figures up but
      // cost a row of height on every ingredient for something usually
      // switched off. The letters do the aligning instead — "28P 0C 3F"
      // reads in any order it lands in, because each number says what it is.
      // Four pills in fixed columns, labelled once by the header above the
      // list rather than by every row carrying its own "P C F". One line,
      // and the colours do the identifying.
      cell.innerHTML = `
        <span class="macro-pill has-parts is-kcal">${row.kcal}<span class="macro-pill-unit">kcal</span></span>
        <span class="macro-pill has-parts" style="background-color: var(--color-protein-soft); color: var(--color-protein)"><span class="macro-pill-label">P</span>${row.protein}<span class="macro-pill-unit">g</span></span>
        <span class="macro-pill has-parts" style="background-color: var(--color-carbs-soft); color: var(--color-carbs)"><span class="macro-pill-label">C</span>${row.carbs}<span class="macro-pill-unit">g</span></span>
        <span class="macro-pill has-parts" style="background-color: var(--color-fat-soft); color: var(--color-fat)"><span class="macro-pill-label">F</span>${row.fat}<span class="macro-pill-unit">g</span></span>`
    })
  }

  // "≈450 g cooked" beside the amount, for a food whose weight changes
  // with cooking. Same chip the recipe page draws (shared/_converted_pill),
  // without the link: the notice on the same row already carries "Why?",
  // and two doors to one explainer on one row is one too many.
  paintConversion(cell, text) {
    if (!cell) return
    if (!text) {
      cell.replaceChildren()
      return
    }

    const chip = document.createElement("span")
    chip.className = "chip whitespace-nowrap"
    chip.style.backgroundColor = "var(--color-nutrition-soft)"
    chip.style.color = "var(--color-nutrition-deep)"
    chip.textContent = text
    cell.replaceChildren(chip)
  }
}
